import Vue from "vue";
import VueRouter, {Route, RouteConfig} from "vue-router";
import Home from "@/features/home/views/Home.vue";
import {
    EducationalRecordDetailsProps,
    TrainingRecordDetailsProps,

} from "@/features/documents/qualifications/records/views/RecordDetailsProps";
import _ from "underscore";
import {
    SeafaringPracticeDetailsProps,
    ShipRepairPracticeDetailsProps,
    InlandVesselPracticeDetailsProps,
    PilotagePracticeDetailsProps,
    VtsCenterPracticeDetailsProps
} from "@/features/documents/practices/PracticeDetailsProps";
import {authService} from "@/AuthService";
import {DuplicableDocumentModelDocumentType} from "@/api-client";
import {tryHandleError} from "../tryHandleError";

Vue.use(VueRouter);

export const createSeafarerProfileRouteName = "create-seafarer-profile";
export const profileSelectionRouteName = "profile-selection";
export const confirmPersonalDataRouteName = "confirm-personal-data";
export const oidcSilentRefreshCallbackName = "oidc-silent-refresh-callback";
export const oidcCallbackName = "OidcCallback";
const accessLinkLoginRouteName = "access-link-login";

const routes: Array<RouteConfig> = [
    {
        path: "/",
        name: "Home",
        component: Home,
    },
    {
        path: "/notifications",
        name: "notifications",
        component: () => import("@/features/notifications/views/Notifications.vue")
    },
    // NOTE: Keep "/oidc-" in OIDC paths.
    {
        path: "/oidc-callback",
        name: oidcCallbackName,
        component: () => import("@/App.vue"),
    },
    {
        path: "/oidc-silent-refresh-callback",
        name: oidcSilentRefreshCallbackName,
        component: () => import("@/App.vue")
    },
    {
        path: "/confirm-personal-data",
        name: confirmPersonalDataRouteName,
        component: () => import(/* webpackChunkName: "seafarers" */"@/features/home/views/ConfirmPersonalData.vue"),
    },
    {
        path: "/select-profile",
        name: profileSelectionRouteName,
        component: () => import(/* webpackChunkName: "seafarers" */"@/features/home/views/ProfileSelection.vue"),
    },
    {
        path: "/seafarer/dashboard",
        name: "seafarer-dashboard",
        component: () => import(/* webpackChunkName: "applications" */"@/features/dashboard/views/SeafarerDashboard.vue"),
    },
    {
        path: "/official/dashboard",
        name: "official-dashboard",
        component: () => import(/* webpackChunkName: "applications" */"@/features/dashboard/views/OfficialDashboard.vue"),
    },
    {
        path: "/applications",
        name: "applications",
        component: () => import(/* webpackChunkName: "applications" */"@/features/applications/views/Applications.vue"),
    },

    {
        path: "/all-certificates",
        name: "official-certificates",
        component: () => import(/* webpackChunkName: "certificates" */"@/features/certificates/views/Certificates.vue"),
    },

    {
        path: "/all-endorsements",
        name: "official-endorsements",
        component: () => import(/* webpackChunkName: "endorsements" */"@/features/endorsements/views/Endorsements.vue"),
    },

    {
        path: "/all-practices",
        name: "official-practices",
        component: () => import(/* webpackChunkName: "practices" */"@/features/practices/views/Practices.vue"),
    },

    {
        path: "/all-medical-certificates",
        name: "official-medical-certificates",
        component: () => import(/* webpackChunkName: "medical-certificates" */"@/features/medicalCertificates/views/MedicalCertificates.vue"),
    },

    {
        path: "/all-qualifications",
        name: "official-qualifications",
        component: () => import(/* webpackChunkName: "qualifications" */"@/features/qualifications/views/Qualifications.vue"),
    },


    {
        path: "/applications/new-certificate-application",
        name: "new-certificate-application",
        component: () => import(/* webpackChunkName: "applications" */"@/features/applications/views/NewApplication.vue"),
    },
    {
        path: "/applications/new-endorsement-application",
        name: "new-endorsement-application",
        component: () => import(/* webpackChunkName: "applications" */"@/features/applications/views/NewEndorsementApplication.vue"),
    },
    {
        path: "/applications/new-concession-permit-application",
        name: "new-concession-permit-application",
        component: () => import(/* webpackChunkName: "applications" */"@/features/applications/views/NewConcessionPermitApplication.vue"),
    },
    {
        path: "/applications/new-duplicate-application",
        name: "new-duplicate-application",
        component: () => import(/* webpackChunkName: "applications" */"@/features/applications/views/NewDuplicate.vue"),
    },
    {
        path: "/swedbank-payment-spa-callback/:applicationId/:requirementId",
        name: "swedbank-payment-callback",
        component: () => import(/* webpackChunkName: "applications" */"@/features/payments/SwedbankPaymentCallback.vue"),
    },
    {
        path: "/representative-organization/:organizationId",
        name: "representative-organization",
        component: () => import(/* webpackChunkName: "applications" */"@/features/organizations/views/RepresentativeOrganization.vue"),
    },
    {
        path: "/applications/:applicationId", // Duplicated in MisWebClientUrlProvider.
        component: () => import(/* webpackChunkName: "applications" */ "@/features/applications/views/ApplicationRoot.vue"),
        children: [
            {
                name: "ApplicationOverview",
                path: '',
                component: () => import(/* webpackChunkName: "applications" */"@/features/applications/views/ApplicationOverview.vue"),
            },
            {
                name: "RequirementOverview",
                path: 'requirements/:requirementId',
                component: () => import(/* webpackChunkName: "applications" */"@/features/applications/views/RequirementOverview.vue"),
            },
            {
                name: "IssueDocumentOverview",
                path: 'issue-document',
                component: () => import(/* webpackChunkName: "applications" */"@/features/applications/views/IssueDocumentOverview.vue")
            },
            {
                name:"requirement-educational-qualification-details",
                path: 'requirements/:requirementId/qualifications/details/educational/:qualificationId',
                component: () => import(/* webpackChunkName: "applications" */ "@/features/documents/qualifications/views/EducationalQualificationDetails.vue"),
                props: {addContainerCss:false, editRouteName: "requirement-educational-qualification-edit"}
            },
            {
                name:"requirement-training-qualification-details",
                path: 'requirements/:requirementId/qualifications/details/training/:qualificationId',
                component: () => import(/* webpackChunkName: "applications" */ "@/features/documents/qualifications/views/TrainingQualificationDetails.vue"),
                props: {addContainerCss:false, editRouteName: "requirement-training-qualification-edit"}
            },
            {
                name:"requirement-certificate-details",
                path:'requirements/:requirementId/certificates/details/:certificateId',
                component: () => import(/* webpackChunkName: "applications" */ "@/features/certificates/CertificateDetails.vue"),
                props: {addContainerCss:false}
            },
            {
                name:"requirement-medical-certificate-details",
                path:'requirements/:requirementId/medical-certificates/details/:id',
                component: () => import(/* webpackChunkName: "applications" */ "@/features/medicalCertificates/views/MedicalCertificate.vue"),
                props: {addContainerCss:false, editRouteName: "requirement-medical-certificate-edit"}
            },
            {
                name:"requirement-ship-repair-practice-details",
                path: 'requirements/:requirementId/:seafarerId/practices/ship-repair/details/:id',
                component: () => import(/* webpackChunkName: "applications" */"@/features/documents/practices/views/PracticeDetails.vue"),
                props: {props: new ShipRepairPracticeDetailsProps("requirement-ship-repair-practice-edit"), addContainerCss:false}
            },
            {
                name:"requirement-seafaring-practice-details",
                path: 'requirements/:requirementId/:seafarerId/practices/seafaring/details/:id',
                component: () => import(/* webpackChunkName: "applications" */"@/features/documents/practices/views/PracticeDetails.vue"),
                props: {props: new SeafaringPracticeDetailsProps("requirement-seafaring-practice-edit"), addContainerCss:false}
            },
            {
                name:"requirement-pilotage-practice-details",
                path: 'requirements/:requirementId/:seafarerId/practices/pilotage/details/:id',
                component: () => import(/* webpackChunkName: "applications" */"@/features/documents/practices/views/PracticeDetails.vue"),
                props: {props: new PilotagePracticeDetailsProps("requirement-pilotage-practice-edit"), addContainerCss:false}
            },
            {
                name:"requirement-inland-vessel-practice-details",
                path: 'requirements/:requirementId/:seafarerId/practices/inland-vessel/details/:id',
                component: () => import(/* webpackChunkName: "applications" */"@/features/documents/practices/views/PracticeDetails.vue"),
                props: {props: new InlandVesselPracticeDetailsProps("requirement-inland-vessel-practice-edit"), addContainerCss:false}
            },
            {
                name:"requirement-vts-center-practice-details",
                path: 'requirements/:requirementId/:seafarerId/practices/vts-center/details/:id',
                component: () => import(/* webpackChunkName: "applications" */"@/features/documents/practices/views/PracticeDetails.vue"),
                props: {props: new VtsCenterPracticeDetailsProps("requirement-vts-center-practice-edit"), addContainerCss:false}
            },
            {
                name:"requirement-educational-qualification-add",
                path: 'requirements/:requirementId/qualifications/educational/add',
                component: () => import(/* webpackChunkName: "applications" */ "@/features/documents/qualifications/views/EducationalQualificationAdd.vue"),
                props: {returnRoute: {name: "RequirementOverview"}},
            },
            {
                name:"requirement-training-qualification-add",
                path: 'requirements/:requirementId/qualifications/training/add',
                component: () => import(/* webpackChunkName: "applications" */ "@/features/documents/qualifications/views/TrainingQualificationAdd.vue"),
                props: {returnRoute: {name: "RequirementOverview"}},
            },
            {
                name:"requirement-seafaring-practice-add",
                path: 'requirements/:requirementId/practices/seafaring/add',
                component: () => import(/* webpackChunkName: "applications" */ "@/features/documents/practices/views/AddSeafaringPractice.vue"),
                props: {returnRoute: {name: "RequirementOverview"}},
            },
            {
                name:"requirement-ship-repair-practice-add",
                path: 'requirements/:requirementId/practices/ship-repair/add',
                component: () => import(/* webpackChunkName: "applications" */ "@/features/documents/practices/views/AddShipRepairPractice.vue"),
                props: {returnRoute: {name: "RequirementOverview"}},
            },
            {
                name:"requirement-pilotage-practice-add",
                path: 'requirements/:requirementId/practices/pilotage/add',
                component: () => import(/* webpackChunkName: "applications" */ "@/features/documents/practices/views/AddPilotagePractice.vue"),
                props: {returnRoute: {name: "RequirementOverview"}},
            },
            {
                name:"requirement-inland-vessel-practice-add",
                path: 'requirements/:requirementId/practices/inland-vessel/add',
                component: () => import(/* webpackChunkName: "applications" */ "@/features/documents/practices/views/AddInlandVesselPractice.vue"),
                props: {returnRoute: {name: "RequirementOverview"}},
            },
            {
                name:"requirement-vts-center-practice-add",
                path: 'requirements/:requirementId/practices/vts-center/add',
                component: () => import(/* webpackChunkName: "applications" */ "@/features/documents/practices/views/AddVtsCenterPractice.vue"),
                props: {returnRoute: {name: "RequirementOverview"}},
            },
            {
                name: "application-summary",
                path: 'summary',
                component: () => import(/* webpackChunkName: "applications" */ "@/features/applications/views/ApplicationSummary.vue"),
            },
            {
                name:"requirement-seafaring-practice-edit",
                path:'requirements/:requirementId/:seafarerId/practices/seafaring/:id/edit',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/documents/practices/views/EditSeafaringPractice.vue"),
                props: {returnRoute: {name: "RequirementOverview"}, props: new SeafaringPracticeDetailsProps()}
            },
            {
                name:"requirement-ship-repair-practice-edit",
                path:'requirements/:requirementId/:seafarerId/practices/ship-repair/:id/edit',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/documents/practices/views/EditShipRepairPractice.vue"),
                props: {returnRoute: {name: "RequirementOverview"}, props: new ShipRepairPracticeDetailsProps()}

            },
            {
                name:"requirement-pilotage-practice-edit",
                path:'requirements/:requirementId/:seafarerId/practices/pilotage/:id/edit',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/documents/practices/views/EditPilotagePractice.vue"),
                props: {returnRoute: {name: "RequirementOverview"}, props: new PilotagePracticeDetailsProps()}
            },
            {
                name:"requirement-inland-vessel-practice-edit",
                path:'requirements/:requirementId/:seafarerId/practices/inland-vessel/:id/edit',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/documents/practices/views/EditInlandVesselPractice.vue"),
                props: {returnRoute: {name: "RequirementOverview"}, props: new InlandVesselPracticeDetailsProps()}
            },
            {
                name:"requirement-vts-center-practice-edit",
                path:'requirements/:requirementId/:seafarerId/practices/vts-center/:id/edit',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/documents/practices/views/EditVtsCenterPractice.vue"),
                props: {returnRoute: {name: "RequirementOverview"}, props: new VtsCenterPracticeDetailsProps()}
            },
            {
                name: "requirement-medical-certificate-edit",
                path: 'requirements/:requirementId/medical-certificates/:id/edit',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/medicalCertificates/views/EditMedicalCertificate.vue"),
                props: {returnRoute: {name: "RequirementOverview"}, addContainerCss:false}
            },
            {
                name: "requirement-educational-qualification-edit",
                path: 'requirements/:requirementId/qualifications/educational/:id/edit',
                component: () => import(/* webpackChunkName: "applications" */ "@/features/documents/qualifications/views/EducationalQualificationEdit.vue"),
                props: {returnRoute: {name: "RequirementOverview"}, addContainerCss:false}
            },
            {
                name: "requirement-training-qualification-edit",
                path: 'requirements/:requirementId/qualifications/training/:id/edit',
                component: () => import(/* webpackChunkName: "applications" */ "@/features/documents/qualifications/views/TrainingQualificationEdit.vue"),
                props: {returnRoute: {name: "RequirementOverview"}, addContainerCss:false}
            },
        ]
    },
    {
        path: "/qualifications/records",
        name: "qualification-records",
        component: () => import(/* webpackChunkName: "qualifications" */"@/features/documents/qualifications/records/views/QualificationRecords.vue"),
    },
    {
        name: 'trainingRecordDetails',
        path: '/qualifications/records/training/details/:id',
        props: {props: new TrainingRecordDetailsProps},
        component: () => import(/* webpackChunkName: "qualifications" */"@/features/documents/qualifications/records/views/QualificationRecordDetails.vue"),
    },
    {
        name: 'trainingRecordAdd',
        path: '/qualifications/records/training/add/:id?',
        component: () => import(/* webpackChunkName: "qualifications" */"@/features/documents/qualifications/records/views/AddTrainingQualificationRecord.vue"),
    },
    {
        name: 'educationalRecordDetails',
        path: '/qualifications/records/educational/details/:id',
        component: () => import(/* webpackChunkName: "qualifications" */"@/features/documents/qualifications/records/views/QualificationRecordDetails.vue"),
        props: {props: new EducationalRecordDetailsProps},
    },
    {
        name: 'educationalRecordAdd',
        path: '/qualifications/records/educational/add/:id?',
        component: () => import(/* webpackChunkName: "qualifications" */"@/features/documents/qualifications/records/views/AddEducationalQualificationRecord.vue"),
    },
    //#region AccessTokenUrlProvider related endpoints
    {
        // Warning: AccessTokenUrlProvider depends on this route.
        path: '/certificate/:accessToken?',
        name: 'certificate-details-by-token',
        component: () => import(/* webpackChunkName: "seafarers" */"@/features/certificates/CertificateDetails.vue")
    },
    {
        // Warning: AccessTokenUrlProvider depends on this route.
        path: '/endorsement/:accessToken?',
        name: 'endorsement-details-by-token',
        component: () => import(/* webpackChunkName: "seafarers" */"@/features/endorsements/EndorsementDetails.vue"),
    },
    {
        // Warning: AccessTokenUrlProvider depends on this route.
        path: '/medical-certificate/:accessToken?',
        name: 'medical-certificate-details-by-token',
        component: () => import(/* webpackChunkName: "seafarers" */"@/features/medicalCertificates/views/MedicalCertificate.vue"),
    },

    {
        // Warning: AccessTokenUrlProvider depends on this route.
        path: '/certificates/:accessToken?',
        name: 'certificates-details-by-token',
        component: () => import(/* webpackChunkName: "seafarers" */"@/features/certificates/CertificateDetails.vue")
    },
    {
        // Warning: AccessTokenUrlProvider depends on this route.
        path: '/endorsements/:accessToken?',
        name: 'endorsements-details-by-token',
        component: () => import(/* webpackChunkName: "seafarers" */"@/features/endorsements/EndorsementDetails.vue"),
    },
    {
        // Warning: AccessTokenUrlProvider depends on this route.
        path: '/medical-certificates/:accessToken?',
        name: 'medical-certificates-details-by-token',
        component: () => import(/* webpackChunkName: "seafarers" */"@/features/medicalCertificates/views/MedicalCertificate.vue"),
    },

    //#endregion

    {
        path: "/seafarers",
        name: "seafarers-search",
        component: () => import(/* webpackChunkName: "seafarers" */"@/features/profiles/views/SearchSeafarer.vue"),
    },
    {
        path: "/seafarers/add/with-personal-code",
        name: "add-seafarer-with-personal-code",
        component: () => import(/* webpackChunkName: "seafarers" */"@/features/profiles/views/AddSeafarerWithPersonalCode.vue"),
    },
    {
        path: "/seafarers/add/without-personal-code",
        name: "add-seafarer-without-personal-code",
        component: () => import(/* webpackChunkName: "seafarers" */"@/features/profiles/views/AddSeafarerWithoutPersonalCode.vue"),
    },
    {
        path: "/seafarers/create-profile",
        name: createSeafarerProfileRouteName,
        component: () => import(/* webpackChunkName: "seafarers" */"@/features/profiles/views/CreateSeafarerProfile.vue"),
    },
    {
        name: "person-profile",
        path: '/seafarers/:seafarerId', // Duplicated in MisWebClientUrlProvider.
        component: () => import(/* webpackChunkName: "seafarers" */'@/features/personProfile/views/PersonProfile.vue'),
        props: {},
        children: [
            {
                path: 'personal-info',
                name: 'personal-info',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/personProfile/views/PersonalInfo.vue"),
            },
            {
                path: 'certificates',
                name: 'certificates',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/personProfile/views/CertificateList.vue"),
            },
            {
                path: 'certificates/add',
                name: 'add-certificates',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/personProfile/views/EnterForeignCertificate.vue"),
            },
            {
                path: 'certificates/:id', // Duplicated in MisWebClientUrlProvider.
                name: 'certificate-details',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/certificates/CertificateDetails.vue"),
            },
            {
                path: "applications/new-certificate-application",
                name: "seafarer-new-certificate-application",
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/applications/views/NewApplication.vue"),
            },
            {
                path: "applications/new-certificate-duplicate-application",
                name: "seafarer-new-certificate-duplicate-application",
                props: { documentType: DuplicableDocumentModelDocumentType.Certificate },
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/applications/views/NewDuplicate.vue"),
            },
            {
                path: 'endorsements',
                name: 'endorsements',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/personProfile/views/EndorsementList.vue")
            },
            {
                path: 'endorsements/:id', // Duplicated in MisWebClientUrlProvider.
                name: 'endorsement-details',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/endorsements/EndorsementDetails.vue"),
            },
            {
                path: "applications/new-endorsement-application",
                name: "seafarer-new-endorsement-application",
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/applications/views/NewEndorsementApplication.vue"),
            },
            {
                path: "applications/new-concession-permit-application",
                name: "seafarer-new-concession-permit-application",
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/applications/views/NewConcessionPermitApplication.vue"),
            },
            {
                path: "applications/new-endorsement-duplicate-application",
                name: "seafarer-new-endorsement-duplicate-application",
                props: { documentType: DuplicableDocumentModelDocumentType.Endorsement },
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/applications/views/NewDuplicate.vue"),
            },
            {
                path: "medical-certificates/add",
                name: "add-medical-certificate",
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/medicalCertificates/views/AddMedicalCertificate.vue"),
            },
            {
                path: 'medical-certificates',
                name: 'medical-certificates',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/personProfile/views/MedicalCertificateList.vue"),
            },
            {
                path: 'medical-certificates/:id',
                name: "medical-certificate-details",
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/medicalCertificates/views/MedicalCertificate.vue"),
            },
            {
                path: 'medical-certificates/:id/edit',
                name: "medical-certificate-edit",
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/medicalCertificates/views/EditMedicalCertificate.vue"),
            },
            {
                path: "qualifications",
                name: "seafarer-qualifications",
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/personProfile/views/QualificationList.vue"),
            },
            {
                name: "educational-add",
                path: 'qualifications/add/educational',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/documents/qualifications/views/EducationalQualificationAdd.vue"),
            },
            {
                name: "educational-details",
                path: 'qualifications/educational/:id',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/documents/qualifications/views/EducationalQualificationDetails.vue"),
            },
            {
                name: "educational-edit",
                path: 'qualifications/educational/:id/edit',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/documents/qualifications/views/EducationalQualificationEdit.vue"),
            },
            {
                name: "training-add",
                path: 'qualifications/add/training',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/documents/qualifications/views/TrainingQualificationAdd.vue"),
            },
            {
                name: "training-details",
                path: 'qualifications/training/:id',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/documents/qualifications/views/TrainingQualificationDetails.vue"),
            },
            {
                name: "training-edit",
                path: 'qualifications/training/:id/edit',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/documents/qualifications/views/TrainingQualificationEdit.vue"),
            },
            {
                path: 'practices',
                name: 'seafarer-practices',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/personProfile/views/PracticeList.vue"),
            },
            {
                path: 'practices/add-ship-repair',
                name:'ship-repair-practice-add',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/documents/practices/views/AddShipRepairPractice.vue"),
            },
            {
                path: 'practices/ship-repair/:id',
                name: 'ship-repair-practice-details',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/documents/practices/views/PracticeDetails.vue"),
                props: {props: new ShipRepairPracticeDetailsProps}
            },
            {
                path: 'practices/ship-repair/:id/edit',
                name: 'ship-repair-practice-edit',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/documents/practices/views/EditShipRepairPractice.vue"),
                props: {props: new ShipRepairPracticeDetailsProps}
            },
            {
                path: 'practices/add-seafaring',
                name:'seafaring-practice-add',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/documents/practices/views/AddSeafaringPractice.vue"),
            },
            {
                path: 'practices/seafaring/:id',
                name: 'seafaring-practice-details',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/documents/practices/views/PracticeDetails.vue"),
                props: {props: new SeafaringPracticeDetailsProps}
            },
            {
                path: 'practices/seafaring/:id/edit',
                name: 'seafaring-practice-edit',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/documents/practices/views/EditSeafaringPractice.vue"),
                props: {props: new SeafaringPracticeDetailsProps()}
            },
            {
                path: 'practices/add-inland-vessel',
                name:'inland-vessel-practice-add',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/documents/practices/views/AddInlandVesselPractice.vue"),
            },
            {
                path: 'practices/inland-vessel/:id',
                name: 'inland-vessel-practice-details',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/documents/practices/views/PracticeDetails.vue"),
                props: {props: new InlandVesselPracticeDetailsProps}
            },
            {
                path: 'practices/inland-vessel/:id/edit',
                name: 'inland-vessel-practice-edit',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/documents/practices/views/EditInlandVesselPractice.vue"),
                props: {props: new InlandVesselPracticeDetailsProps}
            },
            {
                path: 'practices/add-pilotage',
                name:'pilotage-practice-add',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/documents/practices/views/AddPilotagePractice.vue"),
            },
            {
                path: 'practices/pilotage/:id',
                name: 'pilotage-practice-details',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/documents/practices/views/PracticeDetails.vue"),
                props: {props: new PilotagePracticeDetailsProps}
            },
            {
                path: 'practices/pilotage/:id/edit',
                name: 'pilotage-practice-edit',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/documents/practices/views/EditPilotagePractice.vue"),
                props: {props: new PilotagePracticeDetailsProps}
            },
            {
                path: 'practices/add-vts-center',
                name:'vts-center-practice-add',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/documents/practices/views/AddVtsCenterPractice.vue"),
            },
            {
                path: 'practices/vts-center/:id',
                name: 'vts-center-practice-details',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/documents/practices/views/PracticeDetails.vue"),
                props: {props: new VtsCenterPracticeDetailsProps}
            },
            {
                path: 'practices/vts-center/:id/edit',
                name: 'vts-center-practice-edit',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/documents/practices/views/EditVtsCenterPractice.vue"),
                props: {props: new VtsCenterPracticeDetailsProps}
            },
            {
                path: 'all-documents',
                name: 'all-documents',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/personProfile/views/AllDocuments.vue"),
            },
            {
                path: 'shared-resources',
                name: 'seafarer-shared-resources',
                component: () => import(/* webpackChunkName: "seafarers" */"@/features/resourceSharing/SeafarerSharedResources.vue"),
            }
        ]
    },
    {
        path: '/access-link-login',
        name: accessLinkLoginRouteName,
        component: () => import("@/features/resourceSharing/AccessLinkLogin.vue"),
    },
    {
        path: "/login-options",
        name: "login-options",
        component: () => import("@/features/identity/LoginOptions.vue"),
    },
    {
        path: "/stiiliraamat",
        name: "style-guide",
        component: () => import("@/features/styleGuide/views/StyleGuide.vue"),
    },
    {
        path: "/admin",
        name: "admin",
        component: () => import(/* webpackChunkName: "admin" */"@/features/administration/AdministrationLayout.vue"),
        children: [
            {
                path: "organizations",
                name: "organizations",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/organizations/Organizations.vue"),
            },
            {
                path: "organizations/add",
                name: "add-organization",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/organizations/UpsertOrganization.vue")
            },
            {
                path: "organizations/:id",
                name: "admin-organization",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/organizations/AdminOrganization.vue"),
            },
            {
                path: "organizations/:id/edit",
                name: "edit-organization",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/organizations/UpsertOrganization.vue"),
            },
            {
                path: "medical-practitioners",
                name: "medical-practitioners",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/medicalPractitioners/MedicalPractitioners.vue"),
            },
            {
                path: "medical-practitioners/add",
                name: "add-medical-practitioner",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/medicalPractitioners/UpsertMedicalPractitioner.vue")
            },
            {
                path: "medical-practitioners/:id/edit",
                name: "edit-medical-practitioner",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/medicalPractitioners/UpsertMedicalPractitioner.vue")
            },
            {
                path: "areas",
                name: "areas",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/areas/Areas.vue"),
            },
            {
                path: "areas/add",
                name: "add-area",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/areas/UpsertArea.vue")
            },
            {
                path: "areas/:id/edit",
                name: "edit-area",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/areas/UpsertArea.vue")
            },
            {
                path: "ships",
                name: "ships",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/ships/Ships.vue")
            },
            {
                path: "ships/add",
                name: "add-ship",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/ships/UpsertShip.vue")
            },
            {
                path: "ships/:id/edit",
                name: "edit-ship",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/ships/UpsertShip.vue")
            },
            {
                path: "ship-types",
                name: "ship-types",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/shipTypes/ShipTypes.vue")
            },
            {
                path: "ship-types/add",
                name: "add-ship-type",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/shipTypes/UpsertShipType.vue")
            },
            {
                path: "ship-types/:id/edit",
                name: "edit-ship-type",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/shipTypes/UpsertShipType.vue")
            },
            {
                path: "harbours",
                name: "harbours",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/harbours/Harbours.vue")
            },
            {
                path: "harbours/add",
                name: "add-harbour",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/harbours/UpsertHarbour.vue")
            },
            {
                path: "harbours/:id/edit",
                name: "edit-harbour",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/harbours/UpsertHarbour.vue")
            },
            {
                path: "positions",
                name: "positions",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/positions/Positions.vue")
            },
            {
                path: "positions/add",
                name: "add-position",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/positions/UpsertPosition.vue")
            },
            {
                path: "positions/:id/edit",
                name: "edit-position",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/positions/UpsertPosition.vue")
            },
            {
                path: "restrictions",
                name: "restrictions",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/restrictions/Restrictions.vue")
            },
            {
                path: "restrictions/add",
                name: "add-restriction",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/restrictions/UpsertRestriction.vue")
            },
            {
                path: "restrictions/:id/edit",
                name: "edit-restriction",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/restrictions/UpsertRestriction.vue")
            },
            {
                path: "certificate-types/add",
                name: "add-certificate-type",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/certificateTypes/UpsertCertificateType.vue")
            },
            {
                path: "certificate-types/:id/edit",
                name: "edit-certificate-type",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/certificateTypes/UpsertCertificateType.vue")
            },
            {
                path: "certificate-types/:id",
                name: "certificate-type",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/certificateTypes/CertificateType.vue")
            },
            {
                path: "specialities",
                name: "specialities",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/specialities/Specialities.vue")
            },
            {
                path: "specialities/add",
                name: "add-speciality",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/specialities/UpsertSpeciality.vue")
            },
            {
                path: "specialities/:id/edit",
                name: "edit-speciality",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/specialities/UpsertSpeciality.vue")
            },
            {
                path: "certificate-types",
                name: "certificate-types",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/certificateTypes/CertificateTypes.vue")
            },
            {
                path: "certificate-type-templates",
                name: "certificate-type-templates",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/certificateTypeTemplates/CertificateTypeTemplates.vue")
            },
            {
                path: "certificate-type-templates/add",
                name: "add-certificate-type-template",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/certificateTypeTemplates/UpsertCertificateTypeTemplate.vue")
            },
            {
                path: "certificate-type-templates/:id/edit",
                name: "edit-certificate-type-template",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/certificateTypeTemplates/UpsertCertificateTypeTemplate.vue")
            },
            {
                path: "certificate-type-templates/:id",
                name: "certificate-type-template",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/certificateTypeTemplates/CertificateTypeTemplate.vue")
            },

            {
                path: "print-background-templates",
                name: "print-background-templates",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/printBackgroundTemplates/PrintBackgroundTemplates.vue")
            },
            {
                path: "print-background-templates/add",
                name: "add-print-background-template",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/printBackgroundTemplates/UpsertPrintBackgroundTemplate.vue")
            },
            {
                path: "print-background-templates/:id/edit",
                name: "edit-print-background-template",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/printBackgroundTemplates/UpsertPrintBackgroundTemplate.vue")
            },

            {
                path: "application-templates",
                name: "application-templates",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/applicationTemplates/ApplicationTemplates.vue")
            },
            {
                path: "application-templates/add",
                name: "add-application-template",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/applicationTemplates/AddApplicationTemplate.vue")
            },
            {
                path: "application-templates/edit/:id",
                name: "edit-application-template",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/applicationTemplates/EditApplicationTemplate.vue")
            },
            {
                path: "application-templates/:id",
                name: "application-template",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/applicationTemplates/ApplicationTemplate.vue")
            },
            {
                path: "endorsement-print-templates",
                name: "endorsement-print-templates",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/endorsementPrintTemplates/EndorsementPrintTemplates.vue")
            },
            {
                path: "endorsement-print-templates/:type/edit",
                name: "edit-endorsement-print-template",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/endorsementPrintTemplates/EditEndorsementPrintTemplate.vue")
            },
            {
                path: "endorsement-print-templates/:type",
                name: "endorsement-print-template",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/endorsementPrintTemplates/EndorsementPrintTemplate.vue")
            },
            {
                path: "countries",
                name: "countries",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/countries/Countries.vue")
            },
            {
                path: "countries/edit",
                name: "edit-countries",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/countries/EditCountries.vue")
            },
            {
                path: "system-operations",
                name: "system-operations",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/systemOperations/SystemOperations.vue")
            },
            {
                path: "header-notification",
                name: "header-notification",
                component: () => import(/* webpackChunkName: "admin" */"@/features/administration/headerNotification/HeaderNotification.vue")
            }
        ]
    },
    {
        path: "/documents",
        name: "documents-query",
        component: () => import(/* webpackChunkName: "documents" */"@/features/documents/query/SeafarerDocumentsQuery.vue"),
    },
    {
        path: "/documents/validity",
        name: "document-validity-query",
        component: () => import(/* webpackChunkName: "documents" */"@/features/documents/query/DocumentValidityQuery.vue"),
    },
    {
        path: "/forbidden",
        name: "forbidden",
        props: { description: "Ressursi vaatamiseks õigused puuduvad.", statusCode: 403 },
        component: () => import("@/features/home/views/Error.vue"),
    },
    {
        path: "/not-found",
        name: "not-found",
        props: { description: "Lehte ei leitud.", statusCode: 404 },
        component: () => import("@/features/home/views/Error.vue"),
    },
    {
        path: "/server-error",
        name: "server-error",
        props: { description: "Serveripoolne viga. Tegeleme!", statusCode: 500},
        component: () => import("@/features/home/views/Error.vue"),
    },
    // Catch-all 404 Not Found path. Keep this as the last route!
    // Read more: https://router.vuejs.org/guide/essentials/dynamic-matching.html#catch-all-404-not-found-route
    {
        path: "*",
        name: "error-wildcard",
        redirect: { name: 'not-found' }
    }
];

// This is necessary because Vue updates parent route props when navigating between child routes. We don't want that because of meta props which are set in beforeRouteEnter methods.
const addEmptyPropObject = (routeConfig: RouteConfig) => {
    routeConfig.props = routeConfig.props || {};
    if(routeConfig.children){
        _.forEach(routeConfig.children, c => addEmptyPropObject(c));
    }
}
_.forEach(routes, r => addEmptyPropObject(r));

// This is necessary for data preloading before router loads component (prevents jumping, flashing, null initializations, v-if dataloaded etc. nonsense).
// All route.meta.props data assigned in components beforeRouteEnter will be passed on as component props.
const addMetaProps = (routeConfig: RouteConfig) => {
    const previousProps = routeConfig.props;
    if(!previousProps || typeof previousProps === "function"){
        throw `Route: (${routeConfig.name} | ${routeConfig.path}) - Props on router level should be objects. If you need to calculate props, do it in components beforeRouteEnter method and add to meta.props.`;
    }
    routeConfig.props = (route) => {
        const props = previousProps as any;
        if(route.meta!.props){
            _.keys(route.meta!.props).forEach(key => {
               props[key] =  route.meta!.props[key];
            });
        }

        return props;
    };
    if(routeConfig.children){
        _.forEach(routeConfig.children, c => addMetaProps(c));
    }
}
_.forEach(routes, r => addMetaProps(r));

const router = new VueRouter({
    mode: "history",
    base: process.env.BASE_URL,
    routes,

    scrollBehavior (to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        } else {
            return { x: 0, y: 0 };
        }
    },
});

router.onError(error => {
    if (!tryHandleError(error, router)) {
        router.push({ name: "server-error" });
    }
})

const loader = document.getElementById("global-progress-bar")!;
let loaderTimeout!:number;

router.beforeEach(async (to, from, next: Function) => {
    // Add meta props so we can always add props in beforeRouteEnter
    to.meta!.props = to.meta!.props || {};

    clearTimeout(loaderTimeout);
    loaderTimeout = setTimeout(() => {
        loader.style.display = "block";
    }, 200);

    const routesAccessibleWithoutProfile = [profileSelectionRouteName, createSeafarerProfileRouteName, accessLinkLoginRouteName];

    // NOTE: Very important for OIDC silent callback not to get stuck.
    if (to.name == oidcSilentRefreshCallbackName) {
        next();
    }
    else if (to.query.accessLinkToken){
        // Save token to session storage and replace location without token to prevent token from being preserved in history
        const urlWithoutToken = to.fullPath.replace(new RegExp(/accessLinkToken=[a-zA-Z0-9]*&?/gm), "");
        sessionStorage.setItem('mis-access-link-token', to.query.accessLinkToken as string);
        window.location.replace(router.resolve({name: accessLinkLoginRouteName, query: { returnUrl: urlWithoutToken }}).href);
    }
    if (authService.isLoggedIn && !authService.hasProfileSelected() && !_.contains(routesAccessibleWithoutProfile, to.name))
    {
        next({
            name: "profile-selection",
            query: { returnUrl: to.fullPath }
        });
    }
    else if (authService.isLoggedIn && authService.confirmData && authService.hasProfileSelected() && to.name !== confirmPersonalDataRouteName)
    {
        next({
            name: confirmPersonalDataRouteName,
            query: { returnUrl: to.fullPath }
        })
    }
    else
    {
        next();
    }
});

router.afterEach((to: Route, from: Route) => {
    from.meta!.props = {}; // Important! Clear previous route meta props so they wont be reused when navigating back to that route.
    clearTimeout(loaderTimeout);
    loader.style.display = "none";
});

export default router;